import ProfileService from '../services/ProfileService';
import { getSafeRedirectUrl } from './redirectUtils';

const VERIFY_FLOW_STORAGE_KEY = 'labourando:verify-flow-context';
const VERIFY_FLOW_TTL_MS = 15 * 60 * 1000;

export function formatPhoneNumber(value) {
  const cleaned = String(value || '').replace(/[^\d+]/g, '');

  if (cleaned && !cleaned.startsWith('+')) {
    if (cleaned.startsWith('0')) {
      return `+61${cleaned.slice(1)}`;
    }

    if (cleaned.startsWith('61')) {
      return `+${cleaned}`;
    }

    return `+61${cleaned}`;
  }

  return cleaned;
}

export function buildLoginUrl({ phone, returnTo, verified = false } = {}) {
  const params = new URLSearchParams();
  const safeReturnTo = getSafeRedirectUrl(returnTo);

  if (phone) {
    params.set('phone', phone);
  }

  if (safeReturnTo && safeReturnTo !== '/') {
    params.set('returnTo', safeReturnTo);
  }

  if (verified) {
    params.set('verified', '1');
  }

  const query = params.toString();
  return `/login${query ? `?${query}` : ''}`;
}

export function buildVerifyUrl({ phone, returnTo, next } = {}) {
  const params = new URLSearchParams();
  const safeReturnTo = getSafeRedirectUrl(returnTo);

  if (phone) {
    params.set('phone', phone);
  }

  if (safeReturnTo && safeReturnTo !== '/') {
    params.set('returnTo', safeReturnTo);
  }

  if (next === 'login' || next === 'forgot-password') {
    params.set('next', next);
  }

  const query = params.toString();
  return `/verify${query ? `?${query}` : ''}`;
}

export function buildForgotPasswordUrl({ phone, verified = false } = {}) {
  const params = new URLSearchParams();

  if (phone) {
    params.set('phone', phone);
  }

  if (verified) {
    params.set('verified', '1');
  }

  const query = params.toString();
  return `/forgot-password${query ? `?${query}` : ''}`;
}

export function getSignInStep(result) {
  return result?.nextStep?.signInStep || null;
}

export function isSignInStep(result, expectedStep) {
  return getSignInStep(result) === expectedStep;
}

function normalizeAuthErrorName(value) {
  const candidate = String(value || '').trim();
  const match = candidate.match(/([A-Za-z]+Exception)$/);

  return match ? match[1] : candidate;
}

function visitAuthErrorChain(error, visitor, depth = 0) {
  if (!error || depth > 3) {
    return false;
  }

  const candidates = [
    error.name,
    error.code,
    error.__type,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeAuthErrorName(candidate);
    if (normalized && visitor(normalized)) {
      return true;
    }
  }

  return visitAuthErrorChain(error.cause, visitor, depth + 1);
}

function getAuthErrorMessage(error) {
  return String(
    error?.message ||
    error?.cause?.message ||
    ''
  ).toLowerCase();
}

export function isUserNotConfirmedError(error) {
  if (visitAuthErrorChain(error, (name) => name === 'UserNotConfirmedException')) {
    return true;
  }

  return /not confirmed|confirm sign[ -]?up/.test(getAuthErrorMessage(error));
}

export function isPasswordResetRequiredError(error) {
  if (visitAuthErrorChain(error, (name) => name === 'PasswordResetRequiredException')) {
    return true;
  }

  return /password reset required/.test(getAuthErrorMessage(error));
}

function normalizeVerifyFlowContext(context = {}) {
  const safeNext = context.next === 'login' || context.next === 'forgot-password'
    ? context.next
    : 'signup';

  return {
    phone: formatPhoneNumber(context.phone),
    next: safeNext,
    returnTo: getSafeRedirectUrl(context.returnTo),
  };
}

export function persistVerifyFlowContext(context = {}) {
  if (typeof window === 'undefined') {
    return null;
  }

  const normalized = normalizeVerifyFlowContext(context);
  if (!normalized.phone) {
    return null;
  }

  const payload = {
    ...normalized,
    createdAt: Date.now(),
  };

  try {
    window.sessionStorage.setItem(VERIFY_FLOW_STORAGE_KEY, JSON.stringify(payload));
    return payload;
  } catch {
    return payload;
  }
}

function readVerifyFlowContext() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const rawValue = window.sessionStorage.getItem(VERIFY_FLOW_STORAGE_KEY);
    if (!rawValue) {
      return null;
    }

    const parsed = JSON.parse(rawValue);
    if (!parsed?.phone || !parsed?.createdAt) {
      window.sessionStorage.removeItem(VERIFY_FLOW_STORAGE_KEY);
      return null;
    }

    if (Date.now() - parsed.createdAt > VERIFY_FLOW_TTL_MS) {
      window.sessionStorage.removeItem(VERIFY_FLOW_STORAGE_KEY);
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export function getVerifyFlowContext(expectedContext = {}) {
  const stored = readVerifyFlowContext();
  if (!stored) {
    return null;
  }

  const expected = normalizeVerifyFlowContext(expectedContext);
  if (expected.phone && stored.phone !== expected.phone) {
    return null;
  }

  if (expected.next && stored.next !== expected.next) {
    return null;
  }

  return stored;
}

export function clearVerifyFlowContext() {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.removeItem(VERIFY_FLOW_STORAGE_KEY);
  } catch {
    // Ignore storage access failures.
  }
}

export async function resolvePostAuthDestination(returnTo = '/') {
  const safeReturnTo = getSafeRedirectUrl(returnTo);

  try {
    const profile = await new ProfileService().getMyProfile();

    if (!profile || profile.isCompleted === false) {
      return `/onboarding${safeReturnTo !== '/' ? `?returnTo=${encodeURIComponent(safeReturnTo)}` : ''}`;
    }
  } catch {
    return `/onboarding${safeReturnTo !== '/' ? `?returnTo=${encodeURIComponent(safeReturnTo)}` : ''}`;
  }

  return safeReturnTo;
}
